import { takeLatest, call, put, select, takeEvery, debounce, all, fork } from 'redux-saga/effects';
import { addNoteNoApi, addNote, fetchNewNotes } from 'actions/action-notes';
import {
  fetchRelatedTasks,
  setSelectedTasks,
  updateFCDispensingPharmacyInRedux,
} from 'actions/action-tasks';
import { change } from 'redux-form';
import xor from 'lodash/xor';
import uniq from 'lodash/uniq';
import without from 'lodash/without';
import difference from 'lodash/difference';
import { fetchClinicalData } from 'actions/action-clinical-data';
import { ClinicalDataClient } from 'clients/clinical-data';
import { getTaskKey } from 'containers/patient/tasks-new/tasks-table/utils';
import { MED_SYNC_UPDATE_SUCCESS } from 'containers/patient/med-sync/med-sync-actions';
import { updateTherapiesDispensingPharmacyInRedux } from 'actions/action-therapies';
import { calculateNextNeedsByDate } from 'containers/patient/med-sync/med-sync-utils';
import { isAxiosError } from 'axios';
import { logger } from 'winston-logger';
import { windowFeatureIsEnabled } from 'config/window-features';
import {
  DATA_TASKS_REQUEST,
  DATA_TASKS_SUCCESS,
  DATA_TASKS_FAILURE,
  DATA_TASK_COUNTS_FETCH,
  DATA_TASK_COUNTS_REQUEST,
  DATA_TASK_COUNTS_SUCCESS,
  DATA_TASK_COUNTS_FAILURE,
  UPDATE_TASKS_PROPERTIES,
  WORK_LIST_CHANGED,
  FETCH_FC_TASK_DETAILS,
  FETCH_FC_TASK_DETAILS_ERROR,
  FETCH_USER_PREFERENCES,
  ADD_TASK_FILTER_SERVICE_GROUP,
  DELETE_TASK_FILTER_SERVICE_GROUP,
  ADD_THERAPY,
  ADD_THERAPY_AR,
  EDIT_THERAPY,
  MARK_PATIENT_DECEASED,
  SELECTED_PATIENT_ID,
  FETCH_NEW_NOTES,
  REFRESH_THERAPIES_AND_TASKS,
  TASK_STATUS_OUTSTANDING,
  TASK_STATUS_IN_PROGRESS,
  TOGGLE_PIN_WORKFLOW,
  TOGGLE_PIN,
  FETCH_NOTES_SIDEBAR,
  FETCH_NOTES_SIDEBAR_FAILURE,
  ADD_NOTE_COMMUNICATION,
  SEND_SMS_TO_PATIENT_SUCCESS,
  CANCEL_PA_TASK,
  LINK_FINANCIAL_ASSISTANCE,
  SET_SELECTED_TASK_KEYS,
  SET_SHOW_SET_SELECTED_TASKS,
  BULK_TASKS_UPDATED,
  BULK_TASKS_UPDATE_REQUEST,
  INTEGRATE_UPDATED_TASKS,
  FETCH_TASKS,
  DUR_CREATE_NEW_SUCCESS,
  FA_GRANT_NOTIFICATION,
  TASK_LIFECYCLE_ACTIONS,
  SET_DRAFT_TASK,
  REQUEST_DRAFT_TASK,
  SELECT_TASK_KEY,
  TOGGLE_SELECTED_TASK_ROW_KEY,
  CLEAR_SELECTED_TASK_KEY,
  CSL,
  DC,
  FETCH_RELATED_TASK_CLINICAL_DATA,
  DUR,
  ADD_APPOINTMENT_REFERRAL,
  REENROLL_PATIENT,
  FETCH_FC_TASK_DETAILS_RESPONSE,
  FETCH_THERAPY_DISEASE_STATE_SUCCESS,
  FETCH_THERAPY_DISEASE_STATE_ERROR,
  SIDEBAR_FILTERS_SET,
  SET_INITIAL_URL,
  DATA_TASK_COUNTS_INIT_ALL_REQUEST,
  DATA_TASK_COUNTS_INIT_ALL_SUCCESS,
  DATA_TASK_COUNTS_INIT_ALL_FAILURE,
  DATA_TASK_COUNTS_SPECIFIC_REQUEST,
  DATA_TASK_COUNTS_SPECIFIC_SUCCESS,
  DATA_TASK_COUNTS_SPECIFIC_FAILURE,
  TASK_TYPE_CHANGED,
  SIDEBAR_FILTERS_CLEAR_IDS,
  COUNT_MODE_ALL,
  COUNT_MODE_SPECIFIC,
  GENERIC_FILTER_KEYS,
} from '../constants';
import HTTP from '../services/http';
import { getQueryFilters } from '../services/utils/filters-service';
import { fetchPaymentMethodsState } from '../slices/patient-payment-methods-slice';
import { notifyError, notifySuccess, notifyWarning } from '../actions/action-notifications';
import { DraftFillCoordinationClient } from '../clients/draft-fill-coordination-client';
import {
  getIsTaskKeySelected,
  selectDraftTasksState,
  selectSelectedTasks,
} from '../containers/patient/tasks-new/selectors';
import { autoUpsertPatientPbmInsurance } from '../actions/action-eligibility-check';

const CACHE_LIFETIME = 20000;

class TaskSearchKeeper {
  constructor() {
    this.taskResultsPerUrl = {};
    this.previousUrl = null;
    this.callsCount = 0;
  }

  getTasks = async (url, makeRequestFunction, cancelRequestFunction) => {
    const valueInCache = this.taskResultsPerUrl[url];

    if (valueInCache) {
      const expiredCache =
        new Date().getTime() - valueInCache.calledTime.getTime() > CACHE_LIFETIME;

      if (valueInCache.results && !expiredCache) {
        return valueInCache.results;
      }
    }

    this.taskResultsPerUrl[url] = {
      results: null,
      calledTime: new Date(), // check if needed or not
      cancelRequest: cancelRequestFunction,
    };

    if (this.previousUrl && this.taskResultsPerUrl[this.previousUrl] && this.callsCount >= 2) {
      // If there is an ongoing API call (for a different URL or the same, it does not matter)
      // then cancel that previous API call and only make the most recent one
      // We keep track of the calls count, since we always need to wait for the first 2 API calls
      // to have the init data
      this.taskResultsPerUrl[this.previousUrl].cancelRequest();
    }

    // Set the previous URL as the current one for the next search to be done
    this.previousUrl = url;
    this.callsCount += 1;
    const taskResults = await makeRequestFunction(url);
    this.previousUrl = null;

    this.taskResultsPerUrl[url].results = taskResults;
    this.taskResultsPerUrl[url].calledTime = new Date();
    return taskResults;
  };
}

const taskSearchKeeper = new TaskSearchKeeper();
const COUNTS_INIT_DEDUP_MS = 300;
const lastCountsQuerySignature = { queryString: null, timestampMs: 0 };

const fetchTaskCounts = async (sidebarFilters, sidebarTaskType, forceTaskType) => {
  const httpAbortController = new AbortController();
  const requestConfig = { signal: httpAbortController.signal };

  const queryParameterString = getQueryFilters(sidebarFilters, sidebarTaskType, {
    addEncodedTaskTypeStatusIds: true,
  });
  // Given the same set of query params, check if they changed from last time API was called
  const url = `/tasks/count?${queryParameterString}`;

  // Plus, is there is a recent API call made for the same query params, then reuse prev call
  const makeRequestFunction = async _url =>
    HTTP.get(_url, requestConfig).then(payload => ({
      ...payload,
      data: payload.data.tasks,
    }));
  const cancelRequestFunction = () => httpAbortController.abort();

  return taskSearchKeeper.getTasks(url, makeRequestFunction, cancelRequestFunction);
};

const fetchAllTaskCounts = async sidebarFilters => {
  const httpAbortController = new AbortController();
  const requestConfig = { signal: httpAbortController.signal };
  const queryParameterString = getQueryFilters(sidebarFilters, 'therapies', {
    addEncodedTaskTypeStatusIds: true,
    includeAllTaskTypeFilters: true,
    omitTaskParam: true,
  });
  const url = `/tasks/count?task_type=all&${queryParameterString}`;
  const makeRequestFunction = async _url =>
    HTTP.get(_url, requestConfig).then(payload => ({
      ...payload,
      data: payload.data.tasks,
    }));
  const cancelRequestFunction = () => httpAbortController.abort();
  return taskSearchKeeper.getTasks(url, makeRequestFunction, cancelRequestFunction);
};

const stableStringify = obj => {
  if (obj === null || obj === undefined) return String(obj);
  if (typeof obj !== 'object') return JSON.stringify(obj);
  if (Array.isArray(obj)) {
    const sorted = obj
      .map(e => (typeof e === 'object' ? stableStringify(e) : JSON.stringify(e)))
      .sort();
    return `[${sorted.join(',')}]`;
  }
  const keys = Object.keys(obj).sort();
  return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
};

const getAllTaskCountsKeys = (sidebarFilters = null) => {
  const keys = [];
  if (typeof window !== 'undefined') {
    const filtersHash = sidebarFilters ? stableStringify(sidebarFilters) : 'default';
    const baseKey =
      window.USER && window.USER.id ? `taskCounts_${window.USER.id}` : 'taskCounts_default';

    keys.push(`${baseKey}_${filtersHash}`);
    keys.push('taskCounts_default');

    if (window.USER && window.USER.id) {
      keys.push(`taskCounts_${window.USER.id}`);
    }
  }
  return keys;
};

const getCountsFromLocalStorage = (sidebarFilters = null) => {
  try {
    let savedRaw = null;
    for (const k of getAllTaskCountsKeys(sidebarFilters)) {
      savedRaw = localStorage.getItem(k);
      if (savedRaw) break;
    }
    if (!savedRaw) return null;

    const saved = JSON.parse(savedRaw);
    if (!('counts' in saved)) {
      return saved;
    }
    return saved.counts;
  } catch (e) {
    logger.error('Error reading counts from localStorage', e);
  }
  return null;
};

const mergeAndSaveToLocalStorage = (key, countsPartial) => {
  const existingRaw = localStorage.getItem(key);
  let existingCounts = {};
  if (existingRaw) {
    const parsed = JSON.parse(existingRaw);
    existingCounts = parsed?.counts || parsed || {};
  }

  const mergedCounts = { ...existingCounts, ...countsPartial };
  const payload = { counts: mergedCounts };
  localStorage.setItem(key, JSON.stringify(payload));
};

const saveCountsToLocalStorage = (countsPartial, sidebarFilters = null) => {
  const keys = getAllTaskCountsKeys(sidebarFilters);
  if (!keys.length) return;
  try {
    const primaryKey = keys[0];
    mergeAndSaveToLocalStorage(primaryKey, countsPartial);

    keys.slice(1).forEach(k => {
      mergeAndSaveToLocalStorage(k, countsPartial);
    });
  } catch (e) {
    logger.error('Error saving counts to localStorage', e);
  }
};

function* workerFetchTasks(actionBase) {
  const { selectedPatientId } = yield select();

  if (
    actionBase &&
    actionBase.payload &&
    actionBase.payload.data &&
    actionBase.payload.data.post_ar_therapy_add
  ) {
    return;
  }
  if (selectedPatientId) {
    // eslint-disable-next-line max-len
    const url = `/patients/${selectedPatientId}/tasks?categories=${TASK_STATUS_OUTSTANDING},${TASK_STATUS_IN_PROGRESS}&include_today=1&last_reviewed_dur=1`;
    const request = HTTP.get(url);
    yield put({
      type: DATA_TASKS_REQUEST,
      payload: request,
      meta: { patientId: selectedPatientId },
    });

    try {
      const response = yield call(() => request);
      yield put({ type: DATA_TASKS_SUCCESS, payload: response.data.tasks });
    } catch (e) {
      yield put({ type: DATA_TASKS_FAILURE, payload: e });
    }
  }
}

const isFilterActive = taskFilters => {
  if (!taskFilters) return false;
  const {
    completed,
    serviceGroups = [],
    clinics = [],
    displayOnlyFC = [],
    displayOnlyDC = [],
    displayOnlyCSL = [],
    displayOnlyINT = [],
    displayOnlyRS = [],
    fcInProgressReason = [],
    orCategory = [],
    orType = [],
    statusIds = {},
    incidentCategory = [],
    incidentType = [],
    paNewRenewal,
    faNewRenewal,
    cslInitialOngoing,
    lastNamesByLetter,
    fillCycle,
  } = taskFilters;

  const statusIdsActive = Object.values(statusIds).some(arr => Array.isArray(arr) && arr.length);

  return (
    completed ||
    serviceGroups.length ||
    clinics.length ||
    displayOnlyFC.length ||
    displayOnlyDC.length ||
    displayOnlyCSL.length ||
    displayOnlyINT.length ||
    displayOnlyRS.length ||
    fcInProgressReason.length ||
    orCategory.length ||
    orType.length ||
    incidentCategory.length ||
    incidentType.length ||
    statusIdsActive ||
    (Array.isArray(paNewRenewal) ? paNewRenewal.length : paNewRenewal) ||
    (Array.isArray(faNewRenewal) ? faNewRenewal.length : faNewRenewal) ||
    (Array.isArray(cslInitialOngoing) ? cslInitialOngoing.length : cslInitialOngoing) ||
    (lastNamesByLetter && lastNamesByLetter !== 'NONE') ||
    (fillCycle && fillCycle > 1)
  );
};

function* workerTaskCounts(action) {
  const state = yield select();
  const { sidebarFilters, selectedPatientId, sidebarTaskType } = state;

  const sidebarFilterData = sidebarFilters && sidebarFilters.data ? sidebarFilters.data.task : null;

  const localCounts = getCountsFromLocalStorage(sidebarFilterData);

  const effectiveForceTaskType =
    action.payload?.forceTaskType || (localCounts ? sidebarTaskType : undefined);

  const isForce = !!effectiveForceTaskType;

  const hasActiveFilters = isFilterActive(sidebarFilterData);

  if (!isForce && localCounts) {
    yield put({
      type: DATA_TASK_COUNTS_SUCCESS,
      payload: localCounts,
      meta: { patientId: selectedPatientId },
    });
    saveCountsToLocalStorage(localCounts, sidebarFilterData);
    return;
  }

  if (isForce && localCounts) {
    yield put({
      type: DATA_TASK_COUNTS_SUCCESS,
      payload: localCounts,
      meta: { patientId: selectedPatientId },
    });
  }

  yield put({ type: DATA_TASK_COUNTS_REQUEST });
  try {
    const response = yield call(
      fetchTaskCounts,
      sidebarFilterData,
      sidebarTaskType,
      // Force to fetch only this queue, regardless of the current active one
      effectiveForceTaskType,
      action.payload?.skipTimeoutReset,
    );
    let { data } = response;

    const currentCounts = yield select(state => state.taskCounts.data);

    if (
      effectiveForceTaskType &&
      data &&
      !Object.prototype.hasOwnProperty.call(data, effectiveForceTaskType)
    ) {
      data = {
        ...data,
        [effectiveForceTaskType]: {
          ...(currentCounts[effectiveForceTaskType] || {
            task_name: effectiveForceTaskType,
            task_description: effectiveForceTaskType,
          }),
          task_cnt: 0,
        },
      };
    }

    let mergedCounts;
    if (hasActiveFilters && effectiveForceTaskType) {
      mergedCounts = {
        ...currentCounts,
        [effectiveForceTaskType]: data[effectiveForceTaskType] || {
          task_name: effectiveForceTaskType,
          task_description: effectiveForceTaskType,
          task_cnt: 0,
        },
      };
    } else {
      mergedCounts = { ...currentCounts, ...data };
    }

    yield put({
      type: DATA_TASK_COUNTS_SUCCESS,
      payload: mergedCounts,
      meta: { patientId: selectedPatientId },
    });

    saveCountsToLocalStorage(mergedCounts, sidebarFilterData);
  } catch (error) {
    yield put({ type: DATA_TASK_COUNTS_FAILURE });
  }
}

function* workerCountsInitAll() {
  const state = yield select();
  const { sidebarFilters, selectedPatientId } = state;
  const sidebarFilterData = sidebarFilters && sidebarFilters.data ? sidebarFilters.data.task : null;
  yield put({ type: DATA_TASK_COUNTS_INIT_ALL_REQUEST });
  try {
    const currentQueryString = getQueryFilters(sidebarFilterData, 'therapies', {
      addEncodedTaskTypeStatusIds: true,
      includeAllTaskTypeFilters: true,
    });
    if (
      lastCountsQuerySignature.queryString === currentQueryString &&
      Date.now() - lastCountsQuerySignature.timestampMs < COUNTS_INIT_DEDUP_MS
    ) {
      return;
    }
    lastCountsQuerySignature.queryString = currentQueryString;
    lastCountsQuerySignature.timestampMs = Date.now();
    const response = yield call(fetchAllTaskCounts, sidebarFilterData);
    const { data } = response;
    yield put({
      type: DATA_TASK_COUNTS_INIT_ALL_SUCCESS,
      payload: data,
      meta: { patientId: selectedPatientId },
    });
    saveCountsToLocalStorage(data, sidebarFilterData);
  } catch (e) {
    yield put({ type: DATA_TASK_COUNTS_INIT_ALL_FAILURE });
  }
}

function* workerCountsSpecific(action) {
  const state = yield select();
  const { sidebarFilters, selectedPatientId, sidebarTaskType } = state;
  const taskType = action?.payload?.taskType || sidebarTaskType;
  const sidebarFilterData = sidebarFilters && sidebarFilters.data ? sidebarFilters.data.task : null;
  yield put({ type: DATA_TASK_COUNTS_SPECIFIC_REQUEST });
  try {
    const response = yield call(fetchTaskCounts, sidebarFilterData, taskType, taskType);
    const { data } = response;
    const currentCounts = yield select(s => s.taskCounts.data);
    const merged = { ...currentCounts, [taskType]: data[taskType] || currentCounts[taskType] };
    yield put({
      type: DATA_TASK_COUNTS_SPECIFIC_SUCCESS,
      payload: merged,
      meta: { patientId: selectedPatientId },
    });
    saveCountsToLocalStorage(merged, sidebarFilterData);
  } catch (e) {
    yield put({ type: DATA_TASK_COUNTS_SPECIFIC_FAILURE });
  }
}

function* workerFetchTasksListOnly() {
  yield* workerFetchTasks({});
}

function* watcherSidebarFiltersSet(action) {
  const { payload } = action;
  if (payload?.noApiCall) return;
  const nextFilters = payload?.props || {};
  const keys = Object.keys(nextFilters);
  if (nextFilters?.countMode === COUNT_MODE_SPECIFIC) {
    const { sidebarTaskType } = yield select();
    yield* workerCountsSpecific({ payload: { taskType: sidebarTaskType } });
    return;
  }
  const genericChanged =
    nextFilters?.countMode === COUNT_MODE_ALL || keys.some(k => GENERIC_FILTER_KEYS.includes(k));
  if (genericChanged) {
    yield* workerCountsInitAll();
  } else {
    const { sidebarTaskType } = yield select();
    yield* workerCountsSpecific({ payload: { taskType: sidebarTaskType } });
  }
}

function* watcherSidebarFiltersClearIds(action) {
  const { payload } = action;
  const keys = payload?.keys || [];
  const genericChanged = keys.some(k => GENERIC_FILTER_KEYS.includes(k));
  if (genericChanged) {
    yield* workerCountsInitAll();
  } else {
    const { sidebarTaskType } = yield select();
    yield* workerCountsSpecific({ payload: { taskType: sidebarTaskType } });
  }
}

function* workerFetchNewNotesSaga(action) {
  const { payload } = action;
  const newNotes = payload && payload.data && payload.data.notes ? payload.data.notes : [];
  if (newNotes && newNotes.length > 0) {
    yield put(addNoteNoApi(newNotes));
  }
}

function* workerSetNewSMSNotes(action) {
  const { payload } = action;
  if (payload.noteId) {
    yield put(fetchNewNotes({ note_id: payload.noteId }));
  }
}

function* workerFetchNotesOnPin(action) {
  const state = yield select();
  const existingTasks = Object.values(state?.tasks?.data || {});
  const existingTherapies = Object.values(state?.therapies?.data || {});

  const { payload } = action;
  yield put({ type: TOGGLE_PIN, payload });
  try {
    // eslint-disable-next-line
    const toggledNote = payload?.data?.toggled_note;
    const toggledNoteTags = toggledNote.tags;
    const pinnedAtAllLevels =
      toggledNote.changed_is_all_of_type &&
      toggledNoteTags.some(tag => tag.is_pinned === 1) &&
      toggledNoteTags.some(tag => tag.is_all_of_type === 1);
    const unpinnedAtAllLevels =
      toggledNote.changed_is_all_of_type && toggledNoteTags.some(tag => tag.is_pinned === 0);
    if (pinnedAtAllLevels || unpinnedAtAllLevels) {
      yield put({
        type: FETCH_NOTES_SIDEBAR,
        payload: {
          data: {
            notes: state?.notes?.allNotes || [],
            therapies: existingTherapies,
          },
        },
        meta: { patientId: state.selectedPatientId, tasks: existingTasks },
      });
    }
  } catch {
    yield put({ type: FETCH_NOTES_SIDEBAR_FAILURE });
  }
}
function* workerAddCommunicationSaga(action) {
  const { payload } = action;
  const { note, formId, followupDt } = payload;
  try {
    yield put(addNote(note));
    if (followupDt) {
      if (formId) {
        yield put(change(formId, 'followup_dt', followupDt));
      }
    }
  } catch (error) {
    yield put({ type: `${action.type}_ERROR` });
  }
}

function* workerFetchFcTaskDetails(action) {
  if (windowFeatureIsEnabled('fc_completion_role_restriction')) {
    const { payload } = action;
    const state = yield select();
    const {
      selectedPatientId,
      tasks: { data: tasksData, selectedTaskKeys },
    } = state;

    try {
      const shownFcTasks =
        tasksData !== undefined
          ? Object.entries(tasksData)
              .filter(
                ([taskKey, taskObject]) =>
                  taskObject.taskType === 'FC' && selectedTaskKeys.includes(taskKey),
              )
              ?.map(([_, taskObject]) => taskObject)
          : [];
      const shownAnyFcTask = shownFcTasks.length > 0;

      if (action.type === SET_SHOW_SET_SELECTED_TASKS && payload === true && shownAnyFcTask) {
        const fcTaskId = shownFcTasks[0].id;

        yield put({
          type: FETCH_FC_TASK_DETAILS,
          payload: { patientId: selectedPatientId, fcTaskId },
        });
        const request = HTTP.get(`/patients/${selectedPatientId}/tasks/fc/${fcTaskId}/details`);
        const response = yield call(() => request);

        if (response.status === 200) {
          yield put({
            type: FETCH_FC_TASK_DETAILS_RESPONSE,
            payload: { results: response?.data },
          });

          const responseD = response?.data;
          const updatedFcTask = {
            ...shownFcTasks.find(({ id }) => id === Number(responseD.id)),
            roles_authorized_to_complete: responseD.roles_authorized_to_complete,
            taskId: Number(responseD.id),
          };

          yield put({
            type: UPDATE_TASKS_PROPERTIES,
            payload: { tasks: [updatedFcTask] },
          });
        }
      }
    } catch (e) {
      console.error(e);
      yield put({
        type: FETCH_FC_TASK_DETAILS_ERROR,
      });
    }
  }

  return null;
}

/**
 * When tasks are selected and then shown, fetch any payment methods
 * that are needed for displaying any selected FC tasks.
 */
function* workerFetchPaymentMethods(action) {
  const { payload } = action;
  const state = yield select();
  const {
    selectedPatientId,
    tasks: { showSelectedTasks, data: tasksData },
    paymentMethods,
  } = state;

  const isShown = action.type === SET_SHOW_SET_SELECTED_TASKS ? !!payload : !!showSelectedTasks;
  if (!isShown || !selectedPatientId || paymentMethods.loading) {
    return;
  }
  const nextTasks =
    action.type === SET_SELECTED_TASK_KEYS
      ? payload.map(taskKey => tasksData[taskKey])
      : selectSelectedTasks(state);
  if (!nextTasks.length) {
    return;
  }
  const paymentMethodIdsNeeded = uniq(
    nextTasks.flatMap(task =>
      task.taskType === 'FC' && Array.isArray(task.payment_method_ids)
        ? task.payment_method_ids
        : [],
    ),
  );
  const existingPaymentMethodIds = Object.keys(paymentMethods.entities).map(Number);
  const paymentIdsToFetch = difference(paymentMethodIdsNeeded, existingPaymentMethodIds);
  if (paymentIdsToFetch.length) {
    yield put(
      fetchPaymentMethodsState({
        patientId: selectedPatientId,
        paymentMethodIds: paymentIdsToFetch,
      }),
    );
  }
}

function* workerHandleSetSelectedTasks({ type, payload }) {
  const selectedTaskKeys = yield select(state => state.tasks.selectedTaskKeys);
  switch (type) {
    case TOGGLE_SELECTED_TASK_ROW_KEY:
      return yield put(setSelectedTasks(xor(selectedTaskKeys, [].concat(payload)).sort()));
    case CLEAR_SELECTED_TASK_KEY:
      return yield put(setSelectedTasks(without(selectedTaskKeys, ...[].concat(payload)).sort()));
    case SELECT_TASK_KEY:
      return yield put(setSelectedTasks(uniq(selectedTaskKeys.concat(payload)).sort()));
    default:
      break;
  }
  return null;
}

function* workerFetchFCSelfService({ payload }) {
  const { taskIds } = payload;
  // Get FC SS Updated
  const request = HTTP.post('/tasks/fc/fc_ss', { task_ids: taskIds });
  const response = yield call(() => request);

  if (response.status === 200) {
    yield put({
      type: INTEGRATE_UPDATED_TASKS,
      payload: { tasks: response?.data?.tasks },
    });
  }
}

/**
 * Wait for user to stop interacting with tasks before requesting
 * additional payment methods data
 */
function* debounceWorkerFetchPaymentMethods() {
  yield debounce(600, [SET_SELECTED_TASK_KEYS], workerFetchPaymentMethods);
}

function* workerShowUIMessagesForTaskUpdates({ payload }) {
  if (payload.data?.messages?.length) {
    // eslint-disable-next-line no-restricted-syntax
    yield all(
      payload.data.messages.map(message => {
        if (message.type === 'error') {
          return put(notifyError(message.text));
        }
        if (message.type === 'warn') {
          return put(notifyWarning(message.text));
        }
        return put(notifySuccess(message.text));
      }),
    );
  }
}

// ARBOR-11486 - clear these after task is saved or closed
function* workerFetchDraftTasks({ payload }) {
  const draftTaskState = yield select(selectDraftTasksState);
  // Only used for fill-coordination tasks right now.
  const taskKey = `FC${payload.taskId}`;
  if (draftTaskState[taskKey]) {
    // Don't request it again
    return;
  }
  const response = yield call(() => DraftFillCoordinationClient.fetch(payload.taskId));
  if (response.data) {
    yield put({
      type: SET_DRAFT_TASK,
      payload: {
        taskKey,
        data: response.data,
      },
    });
  }
}

function* workerFetchRelatedTaskClinicalData({ payload: task }) {
  const patientId = yield select(state => state.selectedPatientId);
  const isTaskSelected = yield select(state => getIsTaskKeySelected(state, getTaskKey(task)));
  if (isTaskSelected) {
    // eslint-disable-next-line func-names
    yield fork(function* () {
      if (task.taskType === CSL && patientId) {
        yield put(fetchRelatedTasks(task.id, CSL));
        const res = yield call(() => ClinicalDataClient.fetch(patientId));
        yield put(fetchClinicalData(res.data));
      }
      if (task.taskType === DC && patientId) {
        const res = yield call(() => ClinicalDataClient.fetch(patientId));
        yield put(fetchClinicalData(res.data));
      }
    });
  }
}

function* workerUpsertPatientPbmInsuranceFromEligibilityCheck() {
  const patientId = yield select(state => state.selectedPatientId);
  if (patientId) {
    const res = yield call(() => autoUpsertPatientPbmInsurance(patientId));
    yield put(res);
  }
}

const FILL_COORDINATION_TRANSITION = 'FILL_COORDINATION_TRANSITION';
function* workerSetTasksAfterMedSyncUpdate(action) {
  // Update the FC forms after med sync is updated
  const fcForms = yield select(state => state.form[FILL_COORDINATION_TRANSITION]);
  if (fcForms) {
    const { medSyncData } = action.payload;
    // Just look at the form data to see which tasks are relevant
    const formTaskIdsToUpdate = Object.keys(fcForms.values.days_supply);
    // eslint-disable-next-line no-restricted-syntax
    for (const taskId of formTaskIdsToUpdate) {
      // Lookup therapy_id by taskId
      const therapyId = yield select(state => state.tasks.data[`FC${taskId}`?.therapy_id]);
      if (therapyId) {
        // Lookup the new medSyncData by the task's therapy_id
        const medSyncItem = medSyncData.find(medSync => medSync.id === therapyId);
        // If any exists, change the form values
        if (medSyncItem) {
          const { daysSupply, needsbyDate } = medSyncItem;
          yield put(change(FILL_COORDINATION_TRANSITION, `days_supply.${taskId}`, daysSupply));
          yield put(change(FILL_COORDINATION_TRANSITION, `needsby_date.${taskId}`, needsbyDate));
          const nextNeedsByDate = calculateNextNeedsByDate(daysSupply, needsbyDate);
          yield put(
            change(FILL_COORDINATION_TRANSITION, `next_needsby_date.${taskId}`, nextNeedsByDate),
          );
        }
      }
    }
  }
}

function* workerSetDiseaseStates() {
  const state = yield select();
  const { selectedPatientId } = state;
  if (!selectedPatientId) {
    return;
  }
  const tasks = Object.values(state?.tasks?.data || {});
  const cslTasks = tasks.filter(task => task.taskType === 'CSL');
  if (!cslTasks.length) {
    return;
  }
  const therapyIds = uniq(cslTasks.map(task => task.therapy_id).filter(id => !!id));
  if (!therapyIds.length) {
    return;
  }

  const url = `/therapy/disease_state?therapyIds=${therapyIds.join(',')}`;
  const request = HTTP.get(url, {
    cache: true,
    therapyIds: therapyIds.join(','),
  });

  try {
    const response = yield call(() => request);
    yield put({ type: FETCH_THERAPY_DISEASE_STATE_SUCCESS, payload: response.data });
  } catch (e) {
    yield put({ type: FETCH_THERAPY_DISEASE_STATE_ERROR, payload: e });
  }
}

/**
 * workerUpdateTasksBundleRequest()
 * Handles updated tasks response (bundle) after firing editTasks() action (BULK_TASKS_UPDATE_REQUEST)
 */
function* workerUpdateTasksBundleRequest({ payload }) {
  if (payload.data?.updated_tasks) {
    if (payload.data?.updated_tasks.some(task => task.taskType === DUR)) {
      // If DUR is being updated, need to parse out interaction interventions and add to payload
      const interventions =
        payload.data.updated_tasks
          .find(x => x.taskType === DUR)
          ?.interactions?.filter?.(int => int.intervention_id !== null)
          .map(interaction => interaction.intervention) ?? [];
      if (interventions.length) {
        // Create top-level intervention tasks from DUR interactions data
        payload.data.updated_tasks = [...interventions, payload.data.updated_tasks];
      }
    }
    yield put({ type: REFRESH_THERAPIES_AND_TASKS });
    yield put({ type: BULK_TASKS_UPDATED, payload });
    yield put(notifySuccess('Tasks updated successfully.'));
    if (!payload.data?.updatedDispPharmTherapies) {
      return payload;
    }
  }
  if (payload.data?.updatedDispPharmTasks && payload.data?.updatedDispPharmacy) {
    const updatedFCPayload = {
      fcIds: payload.data.updatedDispPharmTasks
        .filter(task => task.taskType === 'FC')
        .map(task => task.id),
      pharmacy: payload.data?.updatedDispPharmacy,
    };
    yield put(updateFCDispensingPharmacyInRedux(updatedFCPayload));
  }

  if (payload.data?.updatedDispPharmTherapies && payload.data?.updatedDispPharmacy) {
    const updatedTherapiesPayload = {
      therapyIds: payload.data.updatedDispPharmTherapies.map(therapy => therapy.id),
      pharmacy: payload.data.updatedDispPharmacy,
    };
    yield put(updateTherapiesDispensingPharmacyInRedux(updatedTherapiesPayload));
    yield put(notifySuccess('Preferred Dispensing Pharmacy Updated Successfully.'));
    return payload;
  }
  logger.error('Update FC task error', payload);
  if (isAxiosError(payload) && payload.response?.data?.message) {
    yield put(notifyError(payload.response?.data?.message));
  } else {
    yield put(notifyError(payload.message ?? 'Unable to edit tasks'));
  }
  return payload;
}

export function* notifyFaLinked() {
  yield put(notifySuccess('Financial Assistance linked successfully.'));
}

export function* watcherTasksSaga() {
  const actions = [
    WORK_LIST_CHANGED,
    ADD_TASK_FILTER_SERVICE_GROUP,
    DELETE_TASK_FILTER_SERVICE_GROUP,
    ADD_THERAPY,
    ADD_THERAPY_AR,
    EDIT_THERAPY,
    MARK_PATIENT_DECEASED,
    REFRESH_THERAPIES_AND_TASKS,
    DUR_CREATE_NEW_SUCCESS,
    FA_GRANT_NOTIFICATION,
  ];
  yield takeEvery(actions, workerTaskCounts);
  yield takeEvery([SET_INITIAL_URL], workerCountsInitAll);
  yield takeEvery([FETCH_USER_PREFERENCES], workerCountsInitAll);
  yield takeEvery([SIDEBAR_FILTERS_SET], watcherSidebarFiltersSet);
  yield takeEvery([SIDEBAR_FILTERS_CLEAR_IDS], watcherSidebarFiltersClearIds);
  yield takeEvery([TASK_TYPE_CHANGED], workerFetchTasksListOnly);
  yield takeLatest(
    [
      BULK_TASKS_UPDATED,
      MARK_PATIENT_DECEASED,
      SELECTED_PATIENT_ID,
      REFRESH_THERAPIES_AND_TASKS,
      EDIT_THERAPY,
      CANCEL_PA_TASK,
      DUR_CREATE_NEW_SUCCESS,
      FA_GRANT_NOTIFICATION,
      REENROLL_PATIENT,
      LINK_FINANCIAL_ASSISTANCE,
    ],
    workerFetchTasks,
  );
  yield takeLatest(FETCH_NEW_NOTES, workerFetchNewNotesSaga);
  yield takeLatest(TOGGLE_PIN_WORKFLOW, workerFetchNotesOnPin);
  yield takeLatest(ADD_NOTE_COMMUNICATION, workerAddCommunicationSaga);
  yield takeEvery(SEND_SMS_TO_PATIENT_SUCCESS, workerSetNewSMSNotes);
  yield takeLatest(
    [TOGGLE_SELECTED_TASK_ROW_KEY, CLEAR_SELECTED_TASK_KEY, SELECT_TASK_KEY],
    workerHandleSetSelectedTasks,
  );
  yield takeLatest([SET_SELECTED_TASK_KEYS], debounceWorkerFetchPaymentMethods);
  yield takeLatest([SET_SHOW_SET_SELECTED_TASKS], workerFetchPaymentMethods);
  yield takeLatest([SET_SHOW_SET_SELECTED_TASKS], workerFetchFcTaskDetails);
  yield takeLatest([FETCH_TASKS], workerFetchFCSelfService);
  yield takeLatest(
    [BULK_TASKS_UPDATED, TASK_LIFECYCLE_ACTIONS.EDIT.FILL_COORDINATION],
    workerShowUIMessagesForTaskUpdates,
  );
  yield takeEvery(REQUEST_DRAFT_TASK, workerFetchDraftTasks);
  yield takeEvery(FETCH_RELATED_TASK_CLINICAL_DATA, workerFetchRelatedTaskClinicalData);
  yield takeLatest(MED_SYNC_UPDATE_SUCCESS, workerSetTasksAfterMedSyncUpdate);
  yield takeLatest(BULK_TASKS_UPDATE_REQUEST, workerUpdateTasksBundleRequest);
  yield takeLatest(ADD_APPOINTMENT_REFERRAL, workerUpsertPatientPbmInsuranceFromEligibilityCheck);
  yield takeLatest([DATA_TASKS_SUCCESS], workerSetDiseaseStates);
  yield takeLatest(LINK_FINANCIAL_ASSISTANCE, notifyFaLinked);
}
